import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Box, Button, CheckBox, DataTable, Layer, Pagination, Spinner } from 'grommet';
import { Close, StatusWarning } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { SearchInput } from '../forms/SearchInput';
import { ApiError, FacilityService } from '/src/api';
import { AddButton, DataTableHeader, DataTableItem, EntityIcon, FormCard, InfoBox, Link, Text } from '/src/components';
import { useGlobalStore, useUserStore } from '/src/context';
import { Facility, ModelName, TClientId, TFacilityId } from '/src/lib/models';
import { toastMessages } from '/src/lib/toast';
import { EntityStatus, TResponseMetadata } from '/src/lib/types';

export type DeleteMultipleFacilitiesModalProps = {
  clientId: TClientId;
  isVisible: boolean;
  setIsVisible: (isVisible: boolean) => void;
  setShouldRefresh: (shouldRefresh: boolean) => void;
};

export const DeleteMultipleFacilitiesModal: React.FC<DeleteMultipleFacilitiesModalProps> = observer(
  ({ clientId, isVisible, setIsVisible, setShouldRefresh }) => {
    /** Context **/
    const userStore = useUserStore();
    const globalStore = useGlobalStore();
    const { t } = useTranslation();

    /** State **/
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSelectingAll, setIsSelectingAll] = useState(false);
    const [facilitiesList, setFacilitiesList] = useState<Facility[]>([]);
    const [selectedFacilityIds, setSelectedFacilityIds] = useState<TFacilityId[]>([]);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit] = useState(20);
    const [metadata, setMetadata] = useState<TResponseMetadata>();
    const [searchQuery, setSearchQuery] = useState('');
    const [committedSearchQuery, setCommittedSearchQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    /** Computed **/
    const canDelete = userStore.isFuseAdmin;

    const selectedFacilityIdSet = new Set(selectedFacilityIds);
    const selectedOnPageCount = facilitiesList.filter((facility) => selectedFacilityIdSet.has(facility.id)).length;
    const isPageSelected = !!facilitiesList.length && selectedOnPageCount === facilitiesList.length;
    const isPagePartiallySelected = selectedOnPageCount > 0 && selectedOnPageCount < facilitiesList.length;
    const hasSelection = !!selectedFacilityIds.length;

    /** Methods **/
    const search = (query: string) => {
      setSearchQuery(query);
      setCommittedSearchQuery(query);
      setIsSearching(query !== committedSearchQuery);
      setCurrentPage(1);
    };

    const loadFacilities = async (page = 1, address?: string) => {
      try {
        setIsLoading(true);
        const { data, meta } = await FacilityService.list({
          client_id: clientId,
          is_active: true,
          is_finalized: false,
          page,
          limit,
          ...(address ? { address_line1: address } : {}),
        });

        setFacilitiesList(data);
        setMetadata(meta);
        setCurrentPage(meta?.current_page ?? page);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.listFacilities.error);
      } finally {
        setIsLoading(false);
        setIsSearching(false);
      }
    };

    const deleteFacilities = async () => {
      if (isDeleting || !selectedFacilityIds.length) return;

      try {
        setIsDeleting(true);

        await FacilityService.delete({ facility_ids: selectedFacilityIds });

        setShouldRefresh(true);
        setIsVisible(false);
        toast.success(toastMessages.deleteFacility.success);
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.deleteFacility.error);
      } finally {
        setIsDeleting(false);
      }
    };

    const toggleSelection = (facilityId: TFacilityId) => {
      setSelectedFacilityIds((prev) =>
        prev.includes(facilityId) ? prev.filter((id) => id !== facilityId) : [...prev, facilityId]
      );
    };

    const toggleSelectPage = (checked: boolean) => {
      const pageIds = facilitiesList.map((facility) => facility.id);
      const pageIdSet = new Set(pageIds);
      setSelectedFacilityIds((prev) => {
        const offPageIds = prev.filter((id) => !pageIdSet.has(id));
        return checked ? [...offPageIds, ...pageIds] : offPageIds;
      });
    };

    const selectAll = async () => {
      if (isSelectingAll || !metadata?.total) return;

      try {
        setIsSelectingAll(true);
        const { data } = await FacilityService.list({
          client_id: clientId,
          is_active: true,
          is_finalized: false,
          page: 1,
          limit: metadata.total,
          ...(committedSearchQuery ? { address_line1: committedSearchQuery } : {}),
        });

        setSelectedFacilityIds((prev) => Array.from(new Set([...prev, ...data.map((facility) => facility.id)])));
      } catch (err) {
        globalStore.handleApiError(err as ApiError, toastMessages.listFacilities.error);
      } finally {
        setIsSelectingAll(false);
      }
    };

    /** Effects **/
    useEffect(() => {
      if (isVisible) {
        setSelectedFacilityIds([]);
        setIsConfirmed(false);
        setCurrentPage(1);
        setSearchQuery('');
        setCommittedSearchQuery('');
      }
    }, [isVisible, clientId]);

    useEffect(() => {
      if (!canDelete && isVisible) setIsVisible(false);
    }, [canDelete, isVisible]);

    useEffect(() => {
      if (isVisible && currentPage) {
        loadFacilities(currentPage, committedSearchQuery || undefined);
      }
    }, [isVisible, currentPage, committedSearchQuery]);

    /** Render **/
    const renderDataTable = () => {
      return (
        <Box elevation="small" round="xsmall" overflow="hidden">
          <DataTable
            pad={{ horizontal: 'small', vertical: 'xsmall' }}
            primaryKey="id"
            columns={[
              {
                property: 'select',
                size: '6%',
                header: (
                  <CheckBox
                    checked={isPageSelected}
                    indeterminate={isPagePartiallySelected}
                    onChange={(e) => toggleSelectPage(e.target.checked)}
                  />
                ),
                render: (facility: Facility) => (
                  <CheckBox
                    checked={selectedFacilityIdSet.has(facility.id)}
                    onChange={() => toggleSelection(facility.id)}
                  />
                ),
              },
              {
                property: 'address_line1',
                size: '30%',
                header: <DataTableHeader title={t('common:address')} />,
                render: (facility: Facility) => (
                  <Box pad={{ vertical: 'small' }}>
                    <Link to={`/clients/${clientId}/facilities/${facility.id}`}>
                      <Box>
                        <Text size="medium" weight={700} color="accent-1" fontFamily="Lato, sans-serif">
                          {facility.address_line1}
                        </Text>
                        <Text size="small" color="accent-1" fontFamily="Lato, sans-serif">
                          {facility.address_city},{' '}
                          {
                            (facility.address_region ?? globalStore.getRegionById(facility.address_region_id))
                              ?.short_code
                          }{' '}
                          {facility.address_post_code}
                        </Text>
                      </Box>
                    </Link>
                  </Box>
                ),
              },
              {
                property: 'is_active',
                align: 'center',
                header: <DataTableHeader title={t('common:status')} />,
                render: (facility: Facility) => (
                  <DataTableItem value={facility.is_active ? EntityStatus.Active : EntityStatus.Retired} />
                ),
              },
              {
                property: 'program.name',
                size: '15%',
                header: <DataTableHeader title={t('common:program')} />,
                render: (facility: Facility) => <DataTableItem value={facility.program?.name || '-'} />,
              },
              {
                property: 'utility.name',
                size: '25%',
                header: <DataTableHeader title={t('facility:utility')} />,
                render: (facility: Facility) => <DataTableItem value={facility.utility?.name || '-'} />,
              },
            ]}
            data={facilitiesList}
            sortable
            background={['light-6', 'white']}
            border={{ color: 'light-2', side: 'bottom', size: 'small' }}
          />
        </Box>
      );
    };

    return isVisible ? (
      <Layer
        position="center"
        onEsc={() => setIsVisible(false)}
        onClickOutside={() => setIsVisible(false)}
        responsive={false}
        style={{
          width: '95%',
          maxWidth: '1400px',
          maxHeight: '95vh',
          overflow: 'auto',
          borderRadius: '8px',
        }}
      >
        <FormCard
          width="100%"
          title={t('facility:deleteFacilities')}
          icon={<EntityIcon entityName={ModelName.Facility} />}
          onSubmit={() => deleteFacilities()}
          isLoading={isLoading || isDeleting}
          hideRequiredText
          saveButtonLabel="DELETE"
          saveButtonLoadingLabel="DELETING..."
          saveButtonDisabled={!isConfirmed || !selectedFacilityIds.length}
          headerChildren={[
            <Button
              key="close"
              pad="none"
              onClick={() => setIsVisible(false)}
              icon={<Close size="20px" />}
              tip={t('common:close')}
            />,
          ]}
        >
          <Box pad={{ horizontal: 'medium', bottom: 'medium' }} gap="medium">
            <InfoBox gap="1rem" direction="row" align="center">
              <StatusWarning size="medium" color="status-warning" />
              <Text>
                {t(
                  'facility:deleteWarning',
                  'Warning: This action will permanently delete the selected facilities. This action cannot be undone.'
                )}
              </Text>
            </InfoBox>
            <Box direction="row" align="end" justify="between">
              {hasSelection && (
                <InfoBox
                  gap="1rem"
                  direction="row"
                  align="center"
                  background={{ color: 'status-error', dark: true }}
                  pad="medium"
                  round="small"
                >
                  <CheckBox checked={isConfirmed} onChange={() => setIsConfirmed(!isConfirmed)} color="white" />
                  <Text color="white">
                    {t('facility:deleteMultipleConfirmation', { count: selectedFacilityIds.length })}
                  </Text>
                </InfoBox>
              )}
              <Box direction="row" align="center" gap="small" margin={{ left: 'auto' }}>
                {hasSelection && (
                  <Text size="small">{t('common:selectedCount', { count: selectedFacilityIds.length })}</Text>
                )}
                {(hasSelection || !!metadata?.total) && (
                  <AddButton
                    label={hasSelection ? t('common:clearAll') : `${t('common:selectAll')} (${metadata?.total})`}
                    onClick={() => {
                      setIsConfirmed(false);
                      if (hasSelection) setSelectedFacilityIds([]);
                      else selectAll();
                    }}
                    disabled={isSelectingAll || isLoading || isSearching}
                  />
                )}
                <SearchInput
                  searchQuery={searchQuery}
                  placeholder={t('facility:searchByAddress', 'Search by Address')}
                  setSearchQuery={setSearchQuery}
                  onSearch={search}
                  isSearching={isSearching}
                />
              </Box>
            </Box>
            <Box gap="small">
              {isLoading ? (
                <Box pad={{ vertical: 'medium' }} height="10vh" fill="horizontal" justify="center" align="center">
                  <Spinner size="medium" />
                </Box>
              ) : facilitiesList.length ? (
                <>
                  {renderDataTable()}
                  {!!metadata && metadata.total > limit && (
                    <Box pad={{ top: 'medium' }} justify="center" align="center">
                      <Pagination
                        alignSelf="center"
                        size="small"
                        page={currentPage}
                        step={metadata.per_page}
                        numberItems={metadata.total}
                        onChange={(e) => setCurrentPage(e.page)}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <Box pad={{ horizontal: 'small', vertical: 'medium' }} background="light-6" align="center">
                  <Text size="medium" fontFamily="Lato, sans-serif">
                    {t('facility:noFacilitiesFound', 'No non-finalized facilities found.')}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </FormCard>
      </Layer>
    ) : (
      <></>
    );
  }
);
